import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    RolesService,
    AuthApiService,
    RoleInterface,
    RoleModel,
    RoleInputModel,
    PermissionModel,
    RoleWithPermissionsModel
} from '@spa/api';

import { RolesFormErrorStatesEnum } from '../enums';
import {
    PermissionGroupedControl,
    PermissionGroupedControlCollection,
    PermissionGroupedSubControl
} from '../classes';

import {
    RolesCheckboxControl,
    RolesNameControl,
    RolesNameSelector,
    RolesPermissionsArraySelector
} from '../../../../assets/descriptors';

import { environment } from '../../../../environments/environment';
import { ConfirmPasswordValidatorKey } from '@spa/validators';
import { PositionInterface } from 'appShared/interfaces';
import { BaseControl, Helper } from 'appShared/classes';

export class RolesForm {
    private readonly rolesService: RolesService;
    private readonly formBuilder: FormBuilder;
    private readonly authApiService: AuthApiService;

    private role: RoleWithPermissionsModel;
    private permissions: PermissionModel[];

    public form: FormGroup;
    public roleNameControl: BaseControl;
    public permissionControls: PermissionGroupedControlCollection;
    public permissionsFormArraySelector: string;
    public controlsSubscriptions: Subscription[];

    constructor(
        permissions: PermissionModel[],
        rolesService: RolesService,
        formBuilder: FormBuilder,
        role: RoleWithPermissionsModel = null,
        authApiService: AuthApiService = null
    ) {
        this.permissions = permissions;

        this.rolesService = rolesService;
        this.formBuilder = formBuilder;
        this.role = role;
        this.authApiService = authApiService;

        this.controlsSubscriptions = [];
        this.permissionsFormArraySelector = RolesPermissionsArraySelector;
    }

    private initPermissionControls(): PermissionGroupedControlCollection {
        const rolePermissionIds = !this.role
            ? []
            : this.role.rolePermissions.map(permission => permission.id);

        return this.createPermissionControlsCollection(rolePermissionIds);
    }

    private createPermissionControlsCollection(
        rolePermissionIds: number[]
    ): PermissionGroupedControlCollection {
        const permissionIds = this.permissions.map(permission => permission.id);

        const allowedAppPermissions = this.getAllowedPermissions();

        const mainPermissions = allowedAppPermissions
            .filter(x => x.permissionString.endsWith(':all'));

        const allowedApps = this.getAllowedApps(allowedAppPermissions);

        this.appendMissingParentPermissions(
            allowedApps,
            allowedAppPermissions,
            mainPermissions);

        const sortedMainPermissions = mainPermissions
            .sort(this.comparePermissionsByApplication());

        const collection = new PermissionGroupedControlCollection();

        sortedMainPermissions.forEach((mainPermission, mainIndex) => {
            const mainControl = RolesCheckboxControl(
                mainPermission.id,
                permissionIds,
                rolePermissionIds);

            collection.addItem(new PermissionGroupedControl(
                mainPermission.permissionString,
                Helper.getCapitalized(mainPermission.category),
                mainPermission.application,
                allowedApps.indexOf(mainPermission.application),
                mainIndex,
                mainControl,
                null,
                false,
                mainPermission.id === 0
            ));

            const childPermissions = allowedAppPermissions
                .filter(x => x.permissionString !== mainPermission.permissionString && x.category === mainPermission.category);

            const sortedChildPermissions = childPermissions
                .sort(this.comparePermissionsByAction());

            let row = -1;
            let col = -1;

            sortedChildPermissions.forEach(childPermission => {
                ({ col, row } = this.getControlPosition(col, row));

                const subControl = this.createSubPermissionControl(
                    mainPermission,
                    childPermission,
                    permissionIds,
                    rolePermissionIds,
                    {
                        row,
                        col
                    });

                collection.addSubItem(subControl.mainControlId, subControl);
            });
        });

        this.showApplicationTitles(collection);

        return collection;
    }

    private getAllowedApps(allowedAppPermissions: PermissionModel[]) {
        let allowedApps: string[] = [];

        allowedAppPermissions.forEach(permission => {
            if (allowedApps.indexOf(permission.application) < 0) {
                allowedApps.push(permission.application);
            }
        });

        allowedApps = allowedApps.sort();

        return allowedApps;
    }

    private showApplicationTitles(collection: PermissionGroupedControlCollection) {
        let lastApplication: string = null;

        collection.getItems().forEach(control => {
            if (control.application !== lastApplication) {
                control.showApplicationTitle = true;
                lastApplication = control.application;
            }
        });
    }

    /** Increment permission control position. */
    private getControlPosition(col: number, row: number): PositionInterface {
        col++;

        if (col === 4) {
            col = 0;
        }

        if (col === 0) {
            row++;
        }

        return { col, row };
    }

    /**
     * Filter out the permissions for not allowed applications.
     * And filter out the top most parent (All) permissions.
     */
    private getAllowedPermissions() {
        return this.permissions
            .filter((permission: PermissionModel) => [
                'PWP',
                'Compliance',
                'Fund Portal',
                'SubscriptionManagement',
            ].indexOf(permission.application) < 0)
            .filter(x => !x.permissionString.endsWith(':all:all'));
    }

    /**
     * Some permission groups has no parent (All) permission.
     * Create dummy parent permissions for the ones has no parent.
     */
    private appendMissingParentPermissions(
        allowedApps: string[],
        allowedAppPermissions: PermissionModel[],
        mainPermissions: PermissionModel[]) {
        allowedApps.forEach(app => {
            const missingPermissions = allowedAppPermissions.filter(x => x.application === app);

            missingPermissions.forEach(missingPermission => {
                const foundPermissions = mainPermissions
                    .filter(x => x.application === app && x.category === missingPermission.category);

                if (foundPermissions.length === 0) {
                    const tempPermission = new PermissionModel({
                        id: 0,
                        application: app,
                        category: missingPermission.category,
                        permission_string: `${app}:${missingPermission.category}:all`.toLowerCase(),
                        permission_claim_type: 1,
                        is_obsolete: true,
                        action: 'All'
                    });

                    mainPermissions.push(tempPermission);
                }
            });
        });
    }

    private comparePermissionsByApplication(): (
        left: PermissionModel,
        right: PermissionModel) => number {
        return (left, right) => {
            if (left.application < right.application) {
                return -1;
            }

            if (left.application > right.application) {
                return 1;
            }

            return this.comparePermissionsByAction()(left, right);
        };
    }

    private comparePermissionsByAction(): (
        left: PermissionModel,
        right: PermissionModel) => number {
        return (left, right) => {
            if (left.permissionString < right.permissionString) {
                return -1;
            }

            if (left.permissionString > right.permissionString) {
                return 1;
            }

            return 0;
        };
    }

    private createSubPermissionControl(
        mainPermission: PermissionModel,
        permission: PermissionModel,
        allPermissionIds: number[],
        currentRolePermissionIds: number[],
        itemPosition: PositionInterface,
    ): PermissionGroupedSubControl {
        const control = RolesCheckboxControl(
            permission.id,
            allPermissionIds,
            currentRolePermissionIds);

        if (mainPermission.id !== 0 &&
            currentRolePermissionIds.indexOf(mainPermission.id) >= 0) {
                control.setChecked(false);
                control.disabled = true;
        }

        return new PermissionGroupedSubControl(
            permission.permissionString,
            Helper.getCapitalized(permission.action),
            control,
            itemPosition,
            mainPermission.permissionString
        );
    }

    private handleSubFormControlValueChanged(
        mainControl: PermissionGroupedControl,
        subControl: PermissionGroupedSubControl,
        value: number
    ): void {
        this.permissionControls.resetErrors();

        if (mainControl.isDummyGroup) {
            if (value !== null) {
                subControl.control.setChecked(true);

                if (mainControl.isCheckedAllSubControls() && !mainControl.mainControl.getChecked()) {
                    mainControl.mainControl.setChecked(true, true);
                }
            }
            else {
                subControl.control.setChecked(false);

                if (mainControl.mainControl.getChecked()) {
                    mainControl.mainControl.setChecked(false, true);
                }
            }
        }
        else {
            subControl.control.setChecked(value !== null);
        }
    }

    private registerRole(name: string, permissions: number[]): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = new RoleInputModel(name, permissions);

        return this.rolesService.register(inputModel)
            .pipe(
                map((response: ApiResponse<ApiError[] | RoleInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<RoleInterface>(
                            response,
                            RolesFormErrorStatesEnum.CreateRoleFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private updateRole(name: string, permissions: number[]): Observable<ApiResponse<ApiError[] | boolean>> {
        const forUpdateRole = new RoleModel();

        forUpdateRole.id = this.role.id;
        forUpdateRole.name = name;

        return this.rolesService.update(forUpdateRole)
            .pipe(
                switchMap((updateRoleResponse: ApiResponse<ApiError[] | null>) => {
                    if (updateRoleResponse.status !== HttpStatusCodesEnum.Ok) {
                        this.initForm();

                        return Helper.errorsHelper().createErrorResponseObservable<null>(
                            updateRoleResponse,
                            RolesFormErrorStatesEnum.UpdateRoleFailed
                        );
                    }

                    this.role.name = forUpdateRole.name;

                    const clonedRole = this.role.serialize();

                    this.role.rolePermissions = this.permissions.filter((permission: PermissionModel) => {
                        return permissions.indexOf(permission.id) >= 0;
                    });

                    return this.rolesService.updatePermissions(this.role.id, this.role)
                        .pipe(
                            switchMap((updatePermissionsResponse: ApiResponse<ApiError[] | null>) => {
                                if (updatePermissionsResponse.status !== HttpStatusCodesEnum.Ok) {
                                    this.role = new RoleWithPermissionsModel(clonedRole);

                                    this.initForm();

                                    return Helper.errorsHelper().createErrorResponseObservable<null>(
                                        updatePermissionsResponse,
                                        RolesFormErrorStatesEnum.UpdateRolePermissionsFailed
                                    );
                                }

                                if (this.authApiService.getAuthenticatedUser().roles.indexOf(this.role.name) >= 0) {
                                    return this.authApiService.syncAuthenticatedUserPermissions(environment.application);
                                }

                                return Helper.createSuccessResponseObservable<boolean>(true);
                            })
                        );
                })
            );
    }

    private deleteRole(id: number): Observable<ApiResponse<ApiError[] | boolean>> {
        return this.rolesService.deleteById(id)
            .pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            RolesFormErrorStatesEnum.DeleteRoleFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    initForm(): void {
        this.roleNameControl = RolesNameControl(this.role && this.role.name);
        this.permissionControls = new PermissionGroupedControlCollection();

        this.permissionControls = this.initPermissionControls();

        const formArrays = this.permissionControls.sortItems()
            .map((control: PermissionGroupedControl, mainControlIndex: number) => {
                const subFormArray = new FormArray([]);

                control.setFormIndex(0);
                control.mainControl.name = 0 + '';
                control.setMainFormIndex(mainControlIndex);

                if (control.isDummyGroup && control.isCheckedAllSubControls()) {
                    control.mainControl.setChecked(true);
                }

                const mainFormControl = new FormControl(control.mainControl.value, control.mainControl.validators);

                const subscription = mainFormControl.valueChanges.subscribe((value: number) => {
                    this.permissionControls.resetErrors();

                    control.toggleSubControls(value !== null);
                });

                this.controlsSubscriptions.push(subscription);

                subFormArray.push(mainFormControl);

                control.getSubControls().map((subControl: PermissionGroupedSubControl, subControlIndex: number) => {
                    subControl.setFormIndex(subControlIndex + 1);
                    subControl.control.name = subControlIndex + 1 + '';

                    const subFormControl = new FormControl(subControl.control.value, subControl.control.validators);

                    const subSubscription = subFormControl.valueChanges
                        .subscribe(this.handleSubFormControlValueChanged.bind(this, control, subControl));

                    this.controlsSubscriptions.push(subSubscription);

                    subFormArray.push(subFormControl);
                });

                return subFormArray;
            });

        this.form = this.formBuilder.group({
            [this.roleNameControl.name]: [this.roleNameControl.value, this.roleNameControl.validators],
            [RolesPermissionsArraySelector]: this.formBuilder.array(formArrays)
        });
    }

    validateForm(
        invalidPermissionsKey: string,
        requiredPermissionsKey: string,
        messageFunction: (controlLabel?: string) => { [key: string]: string }
    ): boolean {
        if (this.form.invalid) {
            const nameErrors = this.form.get(RolesNameSelector).errors;

            if (Object.keys(nameErrors).length > 0) {
                const messages = Object.keys(nameErrors)
                    .map((key: string) => messageFunction(this.roleNameControl.placeholder)[key]);

                this.roleNameControl.setErrors(messages);
            }

            const permissionsFormArray = this.form.get(RolesPermissionsArraySelector);

            const isPermissionsInvalid = permissionsFormArray.invalid;

            if (isPermissionsInvalid) {
                this.permissionControls.setErrors([messageFunction()[invalidPermissionsKey]]);
            }

            const permissionStrings = [].concat(...permissionsFormArray.value).filter((item: string) => item !== null);

            if (permissionStrings.length === 0) {
                this.permissionControls.setError(messageFunction()[requiredPermissionsKey]);
            }

            return false;
        }

        return true;
    }

    createRequest(deleteRole: boolean = false, roleId: number = null): Observable<ApiResponse<ApiError[] | boolean>> {
        if (!deleteRole) {
            const name = this.form.get(RolesNameSelector).value;

            const permissions = [].concat(...this.form.get(RolesPermissionsArraySelector).value)
                .filter((id: number) => id);

            return !this.role ? this.registerRole(name, permissions) : this.updateRole(name, permissions);
        }

        return this.deleteRole(roleId);
    }
}
